import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDT } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const ADJUST_STATUS_CLS = {
  approved: "bg-signal-green/15 text-signal-green",
  rejected: "bg-signal-red/15 text-signal-red",
};

export default function AdjustmentsTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ sku: "", quantity: "", reason: "" });
  const canApprove = ["admin", "warehouse_manager"].includes(user?.role);

  const load = () => api.get("/stock/adjustments").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      await api.post("/stock/adjustments", { sku: form.sku, quantity: parseInt(form.quantity) || 0, reason: form.reason });
      toast.success("Adjustment requested");
      setForm({ sku: "", quantity: "", reason: "" });
      load();
    } catch (e) { toast.error(String(e.response?.data?.detail || "Failed")); }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/stock/adjustments/${id}/${action}`, {});
      toast.success(`Adjustment ${action}d`);
      load();
    } catch (e) { toast.error(String(e.response?.data?.detail || "Failed")); }
  };

  return (
    <div className="space-y-4" data-testid="adjustments-tab">
      <div className="bg-card border border-border rounded-lg p-4 grid grid-cols-2 md:grid-cols-5 gap-2">
        <Input placeholder="SKU" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="adj-sku" />
        <Input type="number" placeholder="Qty (+/−)" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="adj-qty" />
        <div className="col-span-2"><Input placeholder="Reason (mandatory)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="adj-reason" /></div>
        <Button onClick={submit} className="bg-primary text-primary-foreground" data-testid="adj-submit">Request Adjustment</Button>
      </div>
      <div className="bg-card border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="adjustments-table">
          <thead><tr className="border-b border-border text-left">
            {["ID", "SKU", "Qty", "Reason", "Requested By", "Status", "Approver", "At", ""].map((h) => (
              <th key={h} className="px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-foreground whitespace-nowrap">{h}</th>))}
          </tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.adjustment_id} className="border-b border-border/50" data-testid={`adj-row-${a.adjustment_id}`}>
                <td className="px-4 py-2.5 metric text-xs">{a.adjustment_id}</td>
                <td className="px-4 py-2.5 metric font-bold">{a.sku}</td>
                <td className={`px-4 py-2.5 metric ${a.quantity > 0 ? "text-signal-green" : "text-signal-red"}`}>{a.quantity > 0 ? "+" : ""}{a.quantity}</td>
                <td className="px-4 py-2.5 text-xs">{a.reason}</td>
                <td className="px-4 py-2.5 text-xs">{a.requested_by} <span className="text-muted-foreground">({a.requested_role})</span></td>
                <td className="px-4 py-2.5"><span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${ADJUST_STATUS_CLS[a.status] || "bg-signal-amber/15 text-signal-amber"}`}>{a.status}</span></td>
                <td className="px-4 py-2.5 text-xs">{a.approved_by || "—"}</td>
                <td className="px-4 py-2.5 metric text-xs">{fmtDT(a.created_at)}</td>
                <td className="px-4 py-2.5 whitespace-nowrap">
                  {a.status === "pending" && canApprove && a.requested_by_id !== user?.user_id && (
                    <>
                      <Button size="sm" variant="outline" className="mr-1" onClick={() => act(a.adjustment_id, "approve")} data-testid={`adj-approve-${a.adjustment_id}`}>Approve</Button>
                      <Button size="sm" variant="ghost" onClick={() => act(a.adjustment_id, "reject")} data-testid={`adj-reject-${a.adjustment_id}`}>Reject</Button>
                    </>
                  )}
                  {a.status === "pending" && a.requested_by_id === user?.user_id && <span className="text-[10px] font-mono text-muted-foreground">OWN REQUEST</span>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">No adjustments</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
